/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Nikita Tropin <ntropin@yandex.ru>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {Injectable, OnDestroy} from '@angular/core';
import {combineLatest, merge, MonoTypeOperatorFunction, Observable, Subject} from 'rxjs';
import {IssueChecklistAction} from './issue-checklist.action';
import {exhaustMap, filter, map, publishReplay, refCount, scan, startWith} from 'rxjs/operators';
import {IssueChecklistState} from './issue-checklist.state';
import {IssueChecklistService} from './issue-checklist.service';
import {IssueCheckList} from 'compob2b-libs';

const defaultState: IssueChecklistState = {
  items: [],
  loading: false
};

@Injectable({
  providedIn: 'root'
})
export class IssueChecklistStore implements OnDestroy {

  actions$: Subject<IssueChecklistAction> = new Subject<IssueChecklistAction>();

  load$: Observable<IssueChecklistAction> =
    this.actions$.pipe(
      ofType('load'),
      exhaustMap((action: IssueChecklistAction) => this.service.load(action.payload)),
      map(payload => ({
        type: 'load success', payload
      }))
    );

  store$: Observable<IssueChecklistAction> =
    this.actions$.pipe(
      ofType('store'),
      exhaustMap((action: IssueChecklistAction) => this.service.store(action.payload)),
      map(payload => ({
        type: 'store success', payload
      }))
    );

  update$: Observable<IssueChecklistAction> =
    this.actions$.pipe(
      ofType('update'),
      exhaustMap((action: IssueChecklistAction) => this.service.update(action.payload)),
      map(payload => ({
        type: 'update success', payload
      }))
    );

  delete$: Observable<IssueChecklistAction> =
    this.actions$.pipe(
      ofType('delete'),
      exhaustMap((action: IssueChecklistAction) => this.service.update(action.payload)),
      map(payload => ({
        type: 'delete success', payload
      }))
    );

  /**
   * Dispatcher
   */
  dispatcher$: Observable<IssueChecklistAction> =
    merge(this.actions$, this.load$, this.store$, this.update$, this.delete$);

  /**
   * State Reducer
   */
  state$: Observable<IssueChecklistState|IssueChecklistAction> =
    this.dispatcher$.pipe(
      //@ts-ignore
      startWith(defaultState),
      //@ts-ignore
      scan((state: IssueChecklistState, action: IssueChecklistAction): IssueChecklistState => {
        switch (action.type) {
          case 'load':
          case 'store':
          case 'update':
          case 'delete':
            return { ...state, loading: true };
          case 'load success':
            return { ...state, items: action.payload, loading: false };
          case 'store success':
            const items = [];
            items.push(action.payload);
            return {
              ...state,
              items: [ ...state.items, ...items],
              loading: false
            };
          case 'update success':
            const index = state.items.findIndex(item => item.id === action.payload.id);
            if (index !== -1) {
              state.items[index] = action.payload;
            }
            return {...state, loading: false};
          case 'delete success':
            const ind = state.items.findIndex(item => item.id === action.payload.id);
            if (ind !== -1) {
              state.items.splice(ind, 1);
            }
            return {...state, loading: false};
          default:
            return state;
        }
      }),
      publishReplay(1),
      refCount()
    );

  /**
   * Selectors
   */
  items$: Observable<IssueCheckList[]> =
    //@ts-ignore
    this.state$.pipe(map((state: IssueChecklistState) => state.items));
  count$: Observable<number> =
    this.items$.pipe(map((items: any) => items.length));
  checked$: Observable<number> =
    this.items$.pipe(
      map((items: IssueCheckList[]) => items.filter(item => item.checked).length)
    );
  percent$ = combineLatest([this.count$,  this.checked$]).pipe(
    map(([count, checked]) => 0 === count ? 0 : Math.floor(checked * 100 / count))
  );

  loading$: Observable<boolean> =
    //@ts-ignore
    this.state$.pipe(map((state: IssueChecklistState) => state.loading));

  constructor(private service: IssueChecklistService) { }

  ngOnDestroy() {
    this.actions$.complete();
  }

  /**
   * Actions
   */

  load(id: number) { this.dispatch({type: 'load', payload: id}); }

  store(id: number, header: string) { this.dispatch({type: 'store', payload: {id, header}}); }

  delete(issue: IssueCheckList) { this.dispatch({type: 'delete', payload: issue}); }

  update(issue: IssueCheckList) { this.dispatch({type: 'update', payload: issue}); }

  private dispatch(action: IssueChecklistAction): void {
    this.actions$.next(action);
  }
}

function ofType<T extends IssueChecklistAction>(type: string): MonoTypeOperatorFunction<T> {
  return filter((_) => type === _.type);
}
